import format from 'date-fns/format';
import { useState } from 'react';

import { ReportingUserDetails } from '@eluve/api-contract';
import {
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eluve/components';

/** "N users" pill shown next to group rows. */
export const UserCountBadge: React.FC<{ count: number }> = ({ count }) => (
  <span className="shrink-0 rounded-full bg-bg-tertiary px-2 py-0.5 text-xs font-medium text-text-tertiary tabular-nums">
    {count.toLocaleString()} {count === 1 ? 'user' : 'users'}
  </span>
);

const DetailRow: React.FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline justify-between gap-6">
    <Text size="sm" color="tertiary" className="whitespace-nowrap">
      {label}
    </Text>
    <Text size="sm" weight="semibold" className="text-right">
      {value ?? '—'}
    </Text>
  </div>
);

/**
 * Hover card with the user's attributes. The hover AREA is the row's whole
 * first cell, but the card anchors to the label content and opens to its
 * RIGHT — near the cursor, never covering the rows below, and moving into
 * another cell dismisses it. No details → children pass through untouched.
 */
export const UserDetailsHover: React.FC<{
  name: string;
  details?: ReportingUserDetails;
  children: React.ReactNode;
}> = ({ name, details, children }) => {
  const [open, setOpen] = useState(false);
  if (!details) {
    return <>{children}</>;
  }
  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <div
          className="flex w-full min-w-0 cursor-default items-center"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <TooltipTrigger asChild>
            <div className="flex min-w-0 items-center">{children}</div>
          </TooltipTrigger>
        </div>
        <TooltipContent
          side="right"
          align="start"
          className="w-80 border border-border-primary bg-background p-4 text-text-primary shadow-lg"
        >
          <div className="flex flex-col gap-2">
            <Text weight="semibold">{name}</Text>
            <div className="border-t border-border-primary" />
            <DetailRow label="Specialty" value={details.specialty} />
            <DetailRow
              label="First Eluve note completed"
              value={
                details.firstCompletedDate
                  ? format(
                      new Date(`${details.firstCompletedDate}T00:00:00`),
                      'MMM d, yyyy',
                    )
                  : null
              }
            />
            <DetailRow
              label="Default SOAP template"
              value={details.defaultTemplateName}
            />
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
